import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'social-media',
  title: 'Social Media',
  shortTitle: 'Social Media',
  icon: 'Share2',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.pexels.com/photos/3296547/pexels-photo-3296547.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&fit=crop',
  teaser: 'Sichtbarkeit ohne Agentur-Budget: Wie du mit klarer Strategie, effizienter Content-Planung und den richtigen Kennzahlen Social Media für deinen Betrieb nutzbar machst.',
  mainArticle: {
    title: 'Social Media für Gastronomen: Der große Leitfaden',
    takeaways: [
      'Zwei Kanäle konsequent bespielt schlagen fünf halbherzig – meist Instagram plus Google-Profil.',
      'Vier Inhaltssäulen mischen: Produkt, Menschen, Hinter den Kulissen, Information.',
      'Eine Stunde pro Woche mit Batch-Produktion und Vorplanung reicht für einen professionellen Auftritt.',
      'Follower sind eine Eitelkeitskennzahl – gemessen werden Klicks, Reservierungen und Nachrichten.',
    ],
    intro: 'Gäste entscheiden heute auf dem Smartphone, wo sie essen gehen – und oft ist ein Reel, ein Foto oder eine Story der Auslöser. Social Media ist damit kein Zeitvertreib, sondern ein Vertriebskanal. Dieser Leitfaden zeigt, wie du ihn mit begrenzter Zeit professionell bespielst.',
    sections: [
      section(
        'Warum Social Media für die Gastronomie so wichtig ist',
        p('Kaum eine Branche ist so visuell wie die Gastronomie. Ein gutes Foto vom Teller, ein kurzes Video aus der Küche oder ein Blick hinter die Kulissen erzeugen Appetit und Vertrauen – lange bevor der Gast die Tür öffnet. Für viele jüngere Gäste ist Social Media die erste Anlaufstelle bei der Restaurantsuche, noch vor der klassischen Suchmaschine.'),
        p('Gleichzeitig ist Social Media der günstigste Weg, **lokal sichtbar** zu sein: Deine Zielgruppe wohnt oder arbeitet im Umkreis weniger Kilometer – und genau dort lassen sich Inhalte organisch und bezahlt ausspielen.')
      ),
      section(
        'Die passenden Kanäle',
        table(
          ['Kanal', 'Stärke', 'Aufwand', 'Für wen'],
          [
            ['Instagram', 'Visuell, lokal, Reels mit hoher Reichweite', 'Mittel', 'Fast alle Konzepte – der Standardkanal'],
            ['Facebook', 'Ältere Zielgruppe, Events, lokale Gruppen', 'Gering (Inhalte aus Instagram übernehmen)', 'Landgastronomie, Familienbetriebe, Events'],
            ['TikTok', 'Sehr hohe organische Reichweite, junge Zielgruppe', 'Hoch (Videoproduktion)', 'Trendkonzepte, Bars, Streetfood, Städte'],
            ['Google-Unternehmensprofil', 'Wird bei jeder Suche gesehen, Beiträge und Fotos', 'Gering', 'Alle – Pflicht'],
            ['LinkedIn', 'Firmenkunden, Catering, Events, Recruiting', 'Gering', 'Catering, Eventlocations, Systemgastro'],
          ]
        ),
        p('Weniger ist mehr: Zwei Kanäle konsequent bespielt bringen mehr als fünf Kanäle halbherzig. Für die meisten Betriebe ist Instagram plus Google-Unternehmensprofil die richtige Basis.')
      ),
      section(
        'Content-Strategie: Die vier Säulen',
        p('Wer nur Tellerfotos postet, wird schnell langweilig. Erfolgreiche Gastro-Accounts mischen vier Inhaltstypen:'),
        list([
          '**Produkt:** Gerichte, Getränke, Saisonales, neue Karte – appetitlich, nah, mit Tageslicht',
          '**Menschen:** Team, Küche, Inhaber – Gesichter schaffen Bindung und ziehen auch Bewerber an',
          '**Hinter den Kulissen:** Warenlieferung, Vorbereitung, Handwerk – zeigt Qualität und Authentizität',
          '**Information & Anlässe:** Öffnungszeiten, Events, Reservierungshinweise, Feiertage, Aktionen',
        ]),
        tip('Verteile die vier Säulen etwa gleichmäßig über den Monat. Wer merkt, dass „Menschen“-Inhalte immer die meisten Reaktionen bekommen, darf den Anteil erhöhen – die Daten zeigen es dir.')
      ),
      section(
        'Effizienz im Alltag',
        p('Das häufigste Problem ist nicht fehlende Kreativität, sondern fehlende Zeit. Die Lösung liegt in Routine und Vorproduktion:'),
        list([
          '**Batch-Produktion:** Einmal pro Woche 30 – 60 Minuten Fotos und Videos aufnehmen – ausreichend für die ganze Woche',
          '**Vorplanung:** Beiträge über Planungstools zeitgesteuert veröffentlichen',
          '**Vorlagen:** Feste Formate für Wochenkarte, Team-Vorstellung, Event-Ankündigung',
          '**Zuständigkeit:** Eine Person im Team verantwortlich, andere liefern Material',
          '**Stories statt Perfektion:** Der Alltag im 15-Sekunden-Format braucht keine Bearbeitung',
        ]),
        p('Wie eine Wochenroutine in einer Stunde funktioniert, zeigt der Vertiefungsartikel zur Content-Planung.')
      ),
      section(
        'Bezahlte Reichweite lokal einsetzen',
        p('Organische Reichweite schwankt. Mit kleinem Budget lassen sich Beiträge gezielt an Menschen im Umkreis ausspielen – etwa an alle innerhalb von fünf Kilometern zwischen 25 und 55 Jahren, die sich für Restaurants interessieren. Schon **5 – 10 € pro Tag** für ausgewählte Beiträge (neue Karte, Event, Wochenendangebot) bringen oft mehr Reservierungen als jede Printanzeige.'),
        list([
          'Nur Beiträge bewerben, die organisch bereits gut funktionieren',
          'Klares Ziel definieren: Reservierungen, Website-Besuche, Eventanmeldungen',
          'Zeitlich begrenzt und mit Handlungsaufforderung („Jetzt Tisch reservieren“)',
          'Ergebnisse auswerten: Kosten pro Klick, pro Reservierung',
        ])
      ),
      section(
        'Community und Interaktion',
        p('Social Media ist ein Dialog. Wer Kommentare und Nachrichten ignoriert, verschenkt Reichweite und Vertrauen. Antworte zeitnah, freundlich und persönlich – auch auf Kritik. Ermutige Gäste, dich zu markieren, und teile ihre Beiträge (mit Zustimmung) in deinen Stories. Nutzergenerierte Inhalte sind glaubwürdiger als jede eigene Aufnahme.')
      ),
      section(
        'Rechtliche Grundlagen',
        list([
          '**Bildrechte:** Nur eigene Fotos oder Bilder mit Lizenz verwenden; Gäste nicht erkennbar ohne Einwilligung zeigen',
          '**Mitarbeiter:** Schriftliche Einwilligung für Fotos und Videos, mit Widerrufsmöglichkeit',
          '**Musik:** Nur lizenzierte Musik aus den Plattform-Bibliotheken für Business-Accounts nutzen',
          '**Impressum:** Auf jedem Profil verlinkt und mit zwei Klicks erreichbar',
          '**Werbekennzeichnung:** Kooperationen und Gegenleistungen kennzeichnen',
          '**Gewinnspiele:** Teilnahmebedingungen, Datenschutz und Plattformregeln beachten',
        ])
      ),
      section(
        'Kennzahlen und Ziele',
        p('Follower-Zahlen sind eine Eitelkeitskennzahl. Relevant ist, ob Social Media Gäste bringt. Achte auf Reichweite bei lokalen Nutzern, Interaktion, Profilbesuche, Klicks auf Reservierungs- oder Routenlink und – wo möglich – Reservierungen mit Quelle „Instagram“. Welche Kennzahlen wirklich zählen und wie du sie mit dem Geschäft verknüpfst, erklärt der Vertiefungsartikel zu Kennzahlen.')
      ),
      section(
        'Diese Fehler kosten Reichweite',
        list([
          '**Unregelmäßigkeit:** Drei Wochen nichts, dann fünf Beiträge an einem Tag',
          '**Nur Tellerfotos:** Ohne Menschen und Geschichten fehlt die Bindung',
          '**Kein Link zur Handlung:** Reservieren, bestellen, Route – der nächste Schritt muss ein Klick sein',
          '**Kommentare unbeantwortet:** Besonders Fragen zu Öffnungszeiten und Reservierungen',
          '**Falsche Kanäle:** Zeit in Plattformen investieren, auf denen die eigenen Gäste nicht sind',
          '**Perfektionismus:** Authentische Alltagsaufnahmen schlagen gestellte Hochglanzbilder',
        ])
      ),
      section(
        'Kurz beantwortet',
        faq([
          { q: 'Wie oft sollte ich posten?', a: 'Für Instagram haben sich 2 – 4 Beiträge pro Woche plus tägliche Stories bewährt. Wichtiger als die Frequenz ist die Regelmäßigkeit – ein fester Rhythmus, den du dauerhaft halten kannst.' },
          { q: 'Brauche ich eine Agentur?', a: 'Nicht zwingend. Mit klarer Struktur und einer Stunde pro Woche lässt sich ein Gastro-Account gut selbst führen – und authentischer als von außen. Agenturen lohnen sich für Kampagnen, bezahlte Anzeigen oder bei mehreren Standorten.' },
          { q: 'Was tun bei negativen Kommentaren?', a: 'Sachlich und freundlich antworten, Verständnis zeigen, Lösung anbieten und das Gespräch in eine Direktnachricht verlagern. Niemals löschen (außer bei Beleidigungen oder Spam) und niemals öffentlich streiten.' },
          { q: 'Lohnt sich ein eigener Fotograf?', a: 'Für eine Grundausstattung an Bildern (Website, Google-Profil, Speisekarte) ja – einmal im Jahr ein halber Tag. Für den Social-Media-Alltag reicht ein aktuelles Smartphone mit gutem Tageslicht.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'content-planung-redaktionsplan',
      title: 'Content-Planung: Der Redaktionsplan in 60 Minuten pro Woche',
      shortTitle: 'Content-Planung',
      teaser: 'Eine feste Wochenroutine, ein einfacher Redaktionsplan und ein Themenpool: So bespielst du Social Media zuverlässig, ohne dass es zur täglichen Last wird.',
      intro: 'Social Media scheitert in der Gastronomie selten an Ideen, sondern am Alltag. Wer keine Routine hat, postet dann, wenn gerade Zeit ist – also nie. Dieser Artikel zeigt ein System, das mit einer Stunde pro Woche auskommt.',
      sections: [
        section(
          'Die Wochenroutine',
          table(
            ['Zeit', 'Aufgabe', 'Hinweis'],
            [
              ['10 Min.', 'Vorwoche auswerten: Was lief gut, was nicht?', 'Reichweite, Interaktion, Nachrichten'],
              ['15 Min.', 'Themen für die Woche festlegen (3 – 4 Beiträge)', 'Aus dem Themenpool, Säulen mischen'],
              ['25 Min.', 'Fotos und Videos aufnehmen', 'Tageslicht, feste Uhrzeit, Küche einbeziehen'],
              ['10 Min.', 'Beiträge texten und einplanen', 'Planungstool, feste Veröffentlichungszeiten'],
            ]
          ),
          p('Stories aus dem Alltag kommen zusätzlich spontan dazu – sie brauchen keine Planung, nur ein Smartphone in Reichweite.')
        ),
        section(
          'Der Redaktionsplan',
          p('Ein Redaktionsplan ist eine einfache Tabelle mit Datum, Kanal, Säule (Produkt, Menschen, Hinter den Kulissen, Information), Thema, Format, Status und Verantwortlichem. Er verhindert, dass drei Tellerfotos hintereinander erscheinen, und macht sichtbar, welche Anlässe bevorstehen: Feiertage, Saisonstart, Kartenwechsel, Events, Ferien.'),
          tip('Plane feste wiederkehrende Formate: „Montag – Wochenkarte“, „Mittwoch – Team-Mittwoch“, „Freitag – Wochenend-Tipp“. Wiederkehrende Formate sparen Denkzeit und schaffen Erwartung bei den Followern.')
        ),
        section(
          'Der Themenpool',
          p('Sammle Ideen laufend an einem Ort – Notiz-App oder Tabelle. Ein guter Pool enthält jederzeit 20 – 30 Ideen. Beispiele:'),
          list([
            'Das Gericht der Woche und seine Geschichte',
            'Ein Lieferant und seine Produkte',
            'Der Weg eines Gerichts von der Lieferung bis zum Teller',
            'Eine Mitarbeiterin stellt ihr Lieblingsgericht vor',
            'Was passiert eine Stunde vor Öffnung?',
            'Fragen aus Kommentaren beantworten',
            'Saisonale Zutaten und warum sie jetzt auf der Karte stehen',
            'Gäste-Feedback und Bewertungen (mit Zustimmung)',
            'Blick in die Weinkarte oder Barkarte',
            'Reservierungstipps für Stoßzeiten',
          ])
        ),
        section(
          'Formate mit Wirkung',
          list([
            '**Reels / Kurzvideos:** Höchste Reichweite, 7 – 20 Sekunden, Untertitel, Hook in den ersten zwei Sekunden',
            '**Karussell-Beiträge:** Mehrere Bilder – ideal für Karte, Team, Prozesse',
            '**Einzelbild:** Schnell produziert, gut für Ankündigungen',
            '**Stories:** Alltag, Umfragen, Countdown zu Events, Links zur Reservierung',
            '**Google-Beiträge:** Wochenkarte und Aktionen zusätzlich im Unternehmensprofil',
          ])
        ),
        section(
          'Arbeitsteilung im Team',
          p('Eine Person verantwortet Plan und Veröffentlichung – aber alle liefern Material. Richte einen gemeinsamen Ordner ein, in den Küche und Service Fotos und Videos ablegen. Eine kurze Regel („Was auf den Pass geht, wird einmal fotografiert“) sorgt für einen stetigen Nachschub an Bildmaterial – ohne zusätzliche Fototermine.')
        ),
      ],
    },
    {
      slug: 'reels-kurzvideos',
      title: 'Reels und Kurzvideos: Reichweite aus dem Gastro-Alltag',
      shortTitle: 'Reels & Kurzvideos',
      teaser: 'Kurzvideos erreichen ein Vielfaches an Menschen. Zehn Ideen, technische Grundlagen mit dem Smartphone und die wichtigsten Regeln für Aufbau und Schnitt.',
      intro: 'Kurzvideos sind der Reichweitenmotor auf allen Plattformen – und die Gastronomie hat perfekte Voraussetzungen: Feuer, Bewegung, Handwerk, Menschen, Essen. Du brauchst kein Equipment, sondern einen Blick für Momente.',
      sections: [
        section(
          'Warum Kurzvideos so gut funktionieren',
          p('Plattformen spielen Kurzvideos auch an Menschen aus, die dir nicht folgen – im Gegensatz zu Fotobeiträgen, die überwiegend Follower sehen. Ein gutes Reel erreicht in einer Stadt leicht 10.000 bis 50.000 lokale Nutzer. Für Gastronomen ist das die günstigste Neukundengewinnung, die es gibt.')
        ),
        section(
          'Zehn Video-Ideen für jeden Betrieb',
          steps([
            'Das Anrichten eines Gerichts von oben – 10 Sekunden, keine Worte',
            'Der Moment, in dem etwas in die Pfanne kommt (Ton an!)',
            'Zeitraffer: Vom leeren Gastraum bis zum vollen Abend',
            'Der Koch beantwortet in 15 Sekunden eine Gästefrage',
            'Warenlieferung: Was heute frisch kommt',
            'Vorher/Nachher: Rohe Zutaten und fertiges Gericht',
            'Team-Vorstellung in drei Sätzen',
            '„Das bestellen unsere Stammgäste am liebsten“',
            'Ein Getränk wird gemixt oder gezapft – nah und langsam',
            'Blick hinter die Kulissen: Was passiert um 9 Uhr morgens?',
          ])
        ),
        section(
          'Technik: Mit dem Smartphone genügt',
          list([
            '**Licht:** Tageslicht am Fenster oder gleichmäßiges Küchenlicht; kein Gegenlicht, kein Blitz',
            '**Stabilität:** Ellbogen anlegen oder kleines Stativ; ruhige, langsame Bewegungen',
            '**Ton:** Originaltöne (Brutzeln, Zapfen, Schneiden) sind wertvoller als Musik – nah ans Geschehen',
            '**Format:** Hochkant (9:16), Linse vorher reinigen',
            '**Nähe:** Näher als du denkst – Details wirken stärker als Totalen',
          ])
        ),
        section(
          'Aufbau und Schnitt',
          p('Die ersten zwei Sekunden entscheiden, ob jemand weiterschaut. Beginne mit dem stärksten Bild – dem Aufschneiden, dem Flambieren, dem Guss der Sauce – nicht mit dem Logo. Halte das Video kurz (7 – 20 Sekunden), schneide alle Pausen heraus und füge Untertitel hinzu, weil viele ohne Ton schauen. Ein kurzer Text im Bild („Unser Sonntagsbraten entsteht so“) gibt Kontext.'),
          tip('Schneide direkt in der Plattform-App oder in einer einfachen Schnitt-App. Perfektion kostet Zeit und wirkt oft weniger authentisch als ein ehrlicher Alltagsmoment.')
        ),
        section(
          'Musik, Rechte und Frequenz',
          p('Nutze für Business-Accounts ausschließlich die lizenzierte Musikbibliothek der jeweiligen Plattform – kommerzielle Musik aus dem Privatkatalog kann zur Sperrung führen. Zeige Mitarbeiter nur mit schriftlicher Einwilligung, Gäste nur unkenntlich oder mit Zustimmung. Ein bis zwei Reels pro Woche sind ein realistisches Ziel; wichtiger als Menge ist, dass jedes Video einen klaren Moment hat.')
        ),
      ],
    },
    {
      slug: 'social-media-kennzahlen',
      title: 'Social-Media-Kennzahlen: Was du wirklich messen solltest',
      shortTitle: 'Kennzahlen',
      teaser: 'Follower sind nicht gleich Gäste. Welche Kennzahlen zeigen, ob Social Media dein Geschäft voranbringt, und wie du die Verbindung zu Reservierungen und Umsatz herstellst.',
      intro: 'Ohne Messung bleibt Social Media Bauchgefühl. Mit den richtigen fünf Kennzahlen siehst du in zehn Minuten pro Monat, was funktioniert – und kannst deine Stunde pro Woche dort investieren, wo sie Gäste bringt.',
      sections: [
        section(
          'Eitelkeitskennzahlen vs. wirksame Kennzahlen',
          p('Follower-Zahl und Likes fühlen sich gut an, sagen aber wenig darüber, ob jemand deshalb bei dir reserviert. Wirksame Kennzahlen messen Reichweite bei der richtigen Zielgruppe, Handlungen (Klicks, Profilbesuche, Nachrichten) und im besten Fall Reservierungen oder Bestellungen.')
        ),
        section(
          'Die wichtigsten Kennzahlen im Überblick',
          table(
            ['Kennzahl', 'Bedeutung', 'Orientierung'],
            [
              ['Reichweite (lokal)', 'Wie viele Menschen im Umkreis haben Inhalte gesehen?', 'Stetig wachsend; Anteil Nicht-Follower bei Reels über 50 %'],
              ['Interaktionsrate', 'Reaktionen ÷ Reichweite', '3 – 6 % bei Beiträgen ist gut'],
              ['Profilbesuche', 'Wer schaut sich nach einem Beitrag das Profil an?', 'Zeigt Interesse über den Beitrag hinaus'],
              ['Link-Klicks', 'Klicks auf Reservierung, Website, Route, Bestellung', 'Die wichtigste Handlungskennzahl'],
              ['Nachrichten und Kommentare mit Fragen', 'Direkte Kaufabsicht („Habt ihr Samstag noch Platz?“)', 'Antwortzeit unter 1 Stunde in Öffnungszeiten'],
              ['Speicherungen und Teilungen', 'Beitrag wurde gemerkt oder weitergeleitet', 'Starkes Signal für Planungsabsicht'],
            ]
          )
        ),
        section(
          'Die Verbindung zum Geschäft herstellen',
          p('Der schwierigste Schritt ist der Nachweis, dass Social Media Gäste bringt. Mit einfachen Mitteln wird das messbar:'),
          list([
            '**Reservierungslink mit Quelle:** Der Link in der Profilbeschreibung führt auf die Reservierungsseite mit Kennung – das Reservierungssystem zeigt Buchungen aus diesem Kanal',
            '**Frage bei der Reservierung:** „Wie hast du von uns erfahren?“ als optionales Feld',
            '**Aktionscodes:** Ein Code aus dem Beitrag, den der Gast an der Kasse nennt',
            '**Event-Anmeldungen:** Tickets oder Anmeldungen über den Social-Media-Link',
            '**Zeitlicher Zusammenhang:** Reservierungen und Umsatz in den 48 Stunden nach einem reichweitenstarken Beitrag beobachten',
          ])
        ),
        section(
          'Die monatliche Auswertung',
          steps([
            'Die fünf reichweitenstärksten Beiträge ansehen: Was haben sie gemeinsam (Säule, Format, Uhrzeit)?',
            'Die fünf schwächsten Beiträge ansehen: Was fehlt?',
            'Link-Klicks und Reservierungen mit Quelle Social Media notieren',
            'Follower-Wachstum und Anteil lokaler Follower prüfen',
            'Eine Konsequenz für den nächsten Monat ableiten – z. B. mehr Team-Inhalte, andere Uhrzeit',
          ]),
          tip('Führe eine einfache Monatstabelle mit diesen fünf Werten. Nach drei Monaten siehst du Trends, die kein einzelner Beitrag zeigt – und kannst begründet entscheiden, wofür deine Zeit am besten eingesetzt ist.')
        ),
      ],
    },
  ],
};
